import React, { useCallback, useEffect, useState } from 'react';
import client from '../../api/client';
import './AdminPages.css';

const ROLES = ['Owner', 'Admin', 'Manager', 'CSRep', 'User'];

const EMPTY_NEW_USER = {
  email: '',
  firstName: '',
  lastName: '',
  role: 'User',
  password: '',
  googleOnly: true,
};

function UserManager() {
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newUser, setNewUser] = useState(EMPTY_NEW_USER);
  const [creating, setCreating] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('User');
  const [lastInviteUrl, setLastInviteUrl] = useState('');
  const [editing, setEditing] = useState(null);
  const [editingPassword, setEditingPassword] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, i, c] = await Promise.all([
        client.get('/api/admin/users'),
        client.get('/api/admin/users/invites'),
        client.get('/api/customers')
      ]);
      setUsers(u.data?.users || []);
      setInvites(i.data?.invites || []);
      setCustomers(c.data?.customers || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const createUser = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const payload = {
        email: newUser.email.trim(),
        firstName: newUser.firstName.trim(),
        lastName: newUser.lastName.trim(),
        role: newUser.role,
        password: newUser.googleOnly ? null : newUser.password,
      };
      await client.post('/api/admin/users', payload);
      setNewUser(EMPTY_NEW_USER);
      setShowAdd(false);
      await load();
    } catch (err) {
      alert(err.response?.data?.error || 'Could not create user');
    } finally {
      setCreating(false);
    }
  };

  const createInvite = async (e) => {
    e.preventDefault();
    try {
      const res = await client.post('/api/admin/users/invites', { email: inviteEmail, role: inviteRole });
      setLastInviteUrl(res.data?.inviteUrl || '');
      setInviteEmail('');
      setInviteRole('User');
      await load();
    } catch (err) {
      alert(err.response?.data?.error || 'Invite failed');
    }
  };

  const resendInvite = async (id) => {
    try {
      const res = await client.post(`/api/admin/users/invites/${id}/resend`);
      setLastInviteUrl(res.data?.inviteUrl || '');
      await load();
    } catch (err) {
      alert(err.response?.data?.error || 'Resend failed');
    }
  };

  const revokeInvite = async (id) => {
    if (!window.confirm('Revoke this invite?')) return;
    try {
      await client.delete(`/api/admin/users/invites/${id}`);
      await load();
    } catch (err) {
      alert(err.response?.data?.error || 'Revoke failed');
    }
  };

  const startEditing = (u) => {
    setEditing({
      id: u.id,
      email: u.email,
      firstName: u.firstName || '',
      lastName: u.lastName || '',
      role: u.role || 'User',
      isActive: u.isActive,
      hasPassword: u.hasPassword,
      customerIds: (u.customers || []).map(g => g.id),
      csRepCustomerIds: (u.customers || []).filter(g => g.actsAsCsRep).map(g => g.id),
      primaryCustomerId: (u.customers || []).find(g => g.isPrimary)?.id ?? null
    });
    setEditingPassword('');
  };

  const saveEditing = async () => {
    try {
      await client.put(`/api/admin/users/${editing.id}`, {
        firstName: editing.firstName,
        lastName: editing.lastName,
        role: editing.role,
        isActive: editing.isActive,
        customerIds: editing.customerIds,
        csRepCustomerIds: editing.csRepCustomerIds,
        primaryCustomerId: editing.primaryCustomerId
      });
      if (editingPassword) {
        await client.post(`/api/admin/users/${editing.id}/password`, { password: editingPassword });
      }
      setEditing(null);
      setEditingPassword('');
      await load();
    } catch (err) {
      alert(err.response?.data?.error || 'Save failed');
    }
  };

  const clearPassword = async () => {
    if (!editing) return;
    if (!window.confirm('Remove this user\u2019s password? They\u2019ll only be able to sign in with Google.')) return;
    try {
      await client.post(`/api/admin/users/${editing.id}/password`, { password: null });
      setEditing({ ...editing, hasPassword: false });
      setEditingPassword('');
      await load();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to clear password');
    }
  };

  const deactivate = async (id) => {
    if (!window.confirm('Deactivate this user?')) return;
    try {
      await client.delete(`/api/admin/users/${id}`);
      await load();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed');
    }
  };

  const toggleCustomer = (gid) => {
    const has = editing.customerIds.includes(gid);
    if (has) {
      setEditing({
        ...editing,
        customerIds: editing.customerIds.filter(x => x !== gid),
        csRepCustomerIds: editing.csRepCustomerIds.filter(x => x !== gid),
        primaryCustomerId: editing.primaryCustomerId === gid ? null : editing.primaryCustomerId,
      });
    } else {
      // CSReps default to acting as a rep on every customer they're added to.
      const nextCsReps = editing.role === 'CSRep'
        ? [...new Set([...editing.csRepCustomerIds, gid])]
        : editing.csRepCustomerIds;
      setEditing({
        ...editing,
        customerIds: [...editing.customerIds, gid],
        csRepCustomerIds: nextCsReps,
      });
    }
  };

  const toggleActsAsCsRep = (gid) => {
    const has = editing.csRepCustomerIds.includes(gid);
    setEditing({
      ...editing,
      csRepCustomerIds: has
        ? editing.csRepCustomerIds.filter(x => x !== gid)
        : [...editing.csRepCustomerIds, gid],
    });
  };

  const toggleCustomerPrimary = (gid) => {
    const isPrimary = editing.primaryCustomerId === gid;
    if (isPrimary) {
      setEditing({ ...editing, primaryCustomerId: null });
      return;
    }
    const csRepNext = editing.csRepCustomerIds.includes(gid)
      ? editing.csRepCustomerIds
      : [...editing.csRepCustomerIds, gid];
    setEditing({ ...editing, primaryCustomerId: gid, csRepCustomerIds: csRepNext });
  };

  if (loading) return <div className="admin-loading">Loading users…</div>;

  return (
    <div className="admin-page">
      <div className="admin-header">
        <div>
          <h2>User Management</h2>
          <p>Add users, change roles, and assign customers.</p>
        </div>
        <button className="admin-primary-btn" onClick={() => { setShowAdd(!showAdd); setShowInvite(false); }}>
          {showAdd ? 'Cancel' : '+ Add User'}
        </button>
      </div>

      {showAdd && (
        <form className="admin-add-user-form" onSubmit={createUser}>
          <div className="admin-add-user-grid">
            <div className="admin-field">
              <label>Email</label>
              <input
                type="email"
                placeholder="person@vitalengine.com"
                value={newUser.email}
                onChange={e => setNewUser({ ...newUser, email: e.target.value })}
                required
              />
            </div>
            <div className="admin-field">
              <label>First name</label>
              <input
                value={newUser.firstName}
                onChange={e => setNewUser({ ...newUser, firstName: e.target.value })}
              />
            </div>
            <div className="admin-field">
              <label>Last name</label>
              <input
                value={newUser.lastName}
                onChange={e => setNewUser({ ...newUser, lastName: e.target.value })}
              />
            </div>
            <div className="admin-field">
              <label>Role</label>
              <select value={newUser.role} onChange={e => setNewUser({ ...newUser, role: e.target.value })}>
                {ROLES.filter(r => r !== 'Owner').map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          </div>

          <div className="admin-field admin-add-user-auth">
            <label className="admin-inline-check">
              <input
                type="checkbox"
                checked={newUser.googleOnly}
                onChange={e => setNewUser({ ...newUser, googleOnly: e.target.checked })}
              />
              Google sign-in only (no password)
            </label>
            {!newUser.googleOnly && (
              <input
                type="text"
                placeholder="Initial password (min 10 chars, upper + lower + digit)"
                value={newUser.password}
                onChange={e => setNewUser({ ...newUser, password: e.target.value })}
                required={!newUser.googleOnly}
              />
            )}
          </div>

          <div className="admin-add-user-actions">
            <button type="button" onClick={() => { setShowAdd(false); setNewUser(EMPTY_NEW_USER); }}>
              Cancel
            </button>
            <button type="submit" className="admin-primary-btn" disabled={creating}>
              {creating ? 'Creating\u2026' : 'Create user'}
            </button>
          </div>

          <p className="admin-form-hint">
            Google-only users can sign in at any time with their @vitalengine.com account — no email is sent.
            To let someone use a password too, uncheck the box above and enter one; they can change it later from
            their profile.
          </p>
        </form>
      )}

      <details className="admin-invite-details">
        <summary>Need to share a signup link instead? Use an invite</summary>
        <div className="admin-invite-details-body">
          <p className="admin-form-hint">
            Invites create a one-time link the user can visit to set their own name and password.
            Email delivery isn’t wired up yet — you’ll copy the link and share it yourself.
          </p>
          <button className="admin-secondary-btn" onClick={() => setShowInvite(!showInvite)}>
            {showInvite ? 'Cancel invite' : '+ Create invite link'}
          </button>

          {showInvite && (
            <form className="admin-invite-form" onSubmit={createInvite}>
              <input
                type="email"
                placeholder="person@vitalengine.com"
                value={inviteEmail}
                onChange={e => setInviteEmail(e.target.value)}
                required
              />
              <select value={inviteRole} onChange={e => setInviteRole(e.target.value)}>
                {ROLES.filter(r => r !== 'Owner').map(r => <option key={r} value={r}>{r}</option>)}
              </select>
              <button type="submit">Generate link</button>
            </form>
          )}

          {lastInviteUrl && (
            <div className="admin-info">
              <strong>Invite link created.</strong> Copy and send to the invitee:
              <input readOnly value={lastInviteUrl} onFocus={e => e.target.select()} />
            </div>
          )}
        </div>
      </details>

      <section>
        <h3>Active invites</h3>
        {invites.filter(i => !i.acceptedAt).length === 0 ? (
          <p className="admin-empty">No pending invites.</p>
        ) : (
          <table className="admin-table">
            <thead>
              <tr><th>Email</th><th>Role</th><th>Expires</th><th /></tr>
            </thead>
            <tbody>
              {invites.filter(i => !i.acceptedAt).map(inv => (
                <tr key={inv.id}>
                  <td>{inv.email}</td>
                  <td><span className="admin-role-chip">{inv.role}</span></td>
                  <td>{new Date(inv.expiresAt).toLocaleDateString()}</td>
                  <td className="admin-actions">
                    <button onClick={() => resendInvite(inv.id)}>Regenerate link</button>
                    <button className="admin-danger" onClick={() => revokeInvite(inv.id)}>Revoke</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section>
        <h3>Users ({users.length})</h3>
        <table className="admin-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Status</th>
              <th>Auth</th>
              <th>Customers</th>
              <th>Last login</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id}>
                <td>{[u.firstName, u.lastName].filter(Boolean).join(' ') || <em>—</em>}</td>
                <td>{u.email}</td>
                <td><span className="admin-role-chip">{u.role}</span></td>
                <td>
                  {u.isActive
                    ? <span className="admin-status active">Active</span>
                    : <span className="admin-status inactive">Disabled</span>}
                </td>
                <td className="admin-auth-methods">
                  {u.hasPassword && <span title="Password set">🔑</span>}
                  {u.hasGoogleLink && <span title="Google linked">G</span>}
                </td>
                <td>
                  {u.customers?.length > 0
                    ? u.customers.map(g =>
                      <span key={g.id} className={`admin-chip ${g.isPrimary ? 'primary' : ''}`}>{g.name}</span>
                    )
                    : <em>None</em>}
                </td>
                <td>{u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleDateString() : '—'}</td>
                <td className="admin-actions">
                  <button onClick={() => startEditing(u)}>Edit</button>
                  {u.role !== 'Owner' && u.isActive &&
                    <button className="admin-danger" onClick={() => deactivate(u.id)}>Deactivate</button>
                  }
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {editing && (
        <div className="admin-modal-backdrop" onClick={() => setEditing(null)}>
          <div className="admin-modal" onClick={e => e.stopPropagation()}>
            <h3>Edit User</h3>
            <div className="admin-field">
              <label>First name</label>
              <input value={editing.firstName} onChange={e => setEditing({ ...editing, firstName: e.target.value })} />
            </div>
            <div className="admin-field">
              <label>Last name</label>
              <input value={editing.lastName} onChange={e => setEditing({ ...editing, lastName: e.target.value })} />
            </div>
            <div className="admin-field">
              <label>Role</label>
              <select value={editing.role} onChange={e => setEditing({ ...editing, role: e.target.value })}>
                {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div className="admin-field">
              <label>
                <input type="checkbox" checked={editing.isActive} onChange={e => setEditing({ ...editing, isActive: e.target.checked })} />
                Active
              </label>
            </div>

            <div className="admin-field">
              <label>Password</label>
              <div className="admin-password-row">
                <input
                  type="text"
                  placeholder={editing.hasPassword ? 'Leave blank to keep current password' : 'Set a password (optional \u2014 blank = Google sign-in only)'}
                  value={editingPassword}
                  onChange={e => setEditingPassword(e.target.value)}
                />
                {editing.hasPassword && (
                  <button type="button" className="admin-danger" onClick={clearPassword}>
                    Remove password
                  </button>
                )}
              </div>
              <p className="admin-form-hint">
                {editing.hasPassword
                  ? 'This user can sign in with password or Google.'
                  : 'This user can only sign in with Google (@vitalengine.com).'}
              </p>
            </div>

            <div className="admin-field">
              <label>Customers</label>
              <p className="admin-form-hint">
                Selected customers appear on this user's Home dashboard.
                Toggle <strong>Acts as CS rep</strong> to also display them
                on the customer card. Marking a customer <strong>Primary</strong>
                automatically flags this user as a CS rep there.
              </p>
              <div className="admin-chips-select">
                {customers.map(g => {
                  const selected = editing.customerIds.includes(g.id);
                  const primary = editing.primaryCustomerId === g.id;
                  const actsAsCsRep = editing.csRepCustomerIds.includes(g.id) || primary;
                  return (
                    <label key={g.id} className={`admin-select-chip ${selected ? 'selected' : ''}`}>
                      <input type="checkbox" checked={selected} onChange={() => toggleCustomer(g.id)} />
                      <span>{g.name}</span>
                      {selected && (
                        <>
                          <button
                            type="button"
                            className={`admin-primary-toggle ${actsAsCsRep ? 'active' : ''}`}
                            disabled={primary}
                            onClick={() => toggleActsAsCsRep(g.id)}
                            title={primary ? 'Primary always acts as CS rep' : 'Show on the customer card'}
                          >{actsAsCsRep ? '✓ acts as CS rep' : '+ acts as CS rep'}</button>
                          <button
                            type="button"
                            className={`admin-primary-toggle ${primary ? 'active' : ''}`}
                            onClick={() => toggleCustomerPrimary(g.id)}
                          >{primary ? '★ primary' : 'make primary'}</button>
                        </>
                      )}
                    </label>
                  );
                })}
                {customers.length === 0 && <em>No customers yet.</em>}
              </div>
            </div>

            <div className="admin-modal-actions">
              <button onClick={() => { setEditing(null); setEditingPassword(''); }}>Cancel</button>
              <button className="admin-primary-btn" onClick={saveEditing}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserManager;
